"use client";

import { useTranslations } from "next-intl";

const SPEC_KEYS = [
	{ labelKey: "maxPartSize" },
	{ labelKey: "minPartSize" },
	{ labelKey: "generalTolerances" },
	{ labelKey: "leadTime" },
];

export interface CapabilitiesTableRow {
	labelKey: string;
}

interface CapabilitiesProps {
	translationNamespace?: string;
	multiColumn?: boolean;
	rowKeys?: CapabilitiesTableRow[];
	columnCount?: 2 | 3;
	titleKeys?: string[];
}

export default function Capabilities({
	translationNamespace = "fiveAxisCapabilities",
	multiColumn = false,
	rowKeys = SPEC_KEYS,
	columnCount = 3,
	titleKeys = ["title"],
}: CapabilitiesProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="mt-10 w-full bg-[#f5f9e8] py-8 sm:py-10 md:mt-20 md:py-12 lg:py-16">
			<div className="container">
				<div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:gap-8 2xl:gap-x-44">
					<div className="flex flex-shrink-0">
						<h2 className="max-w-[280px] text-2xl font-bold leading-tight text-black sm:max-w-[320px] sm:text-[30px] md:max-w-[450px] md:text-4xl">
							{titleKeys.map((key, index) => (
								<span key={key} className={index === titleKeys.length - 1 ? "flex items-end gap-4" : "block"}>
									{t(key)}
									{index === titleKeys.length - 1 ? (
										<span className="h-1 w-12 flex-shrink-0 bg-themePrimary400 md:w-24" />
									) : null}
								</span>
							))}
						</h2>
					</div>

					<div className="flex-1">
						<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
					</div>
				</div>

				<div className="mt-10 md:mt-20">
					{multiColumn ? (
						<div className="w-full overflow-x-auto">
							<table className="w-full min-w-[600px]">
								<thead>
									<tr className="border-b border-gray-300">
										<th className="w-[25%] pb-6 text-left text-lg font-bold text-black md:text-xl"></th>
										<th className="w-[25%] pb-6 text-left text-lg font-bold text-black md:text-xl">
											{t("col1Title")}
										</th>
										<th className="w-[25%] pb-6 text-left text-lg font-bold text-black md:text-xl">
											{t("col2Title")}
										</th>
										{columnCount >= 3 && (
											<th className="w-[25%] pb-6 text-left text-lg font-bold text-black md:text-xl">
												{t("col3Title")}
											</th>
										)}
									</tr>
								</thead>
								<tbody>
									{rowKeys.map((spec) => (
										<tr key={spec.labelKey} className="border-t border-gray-300">
											<td className="py-5 pr-4 text-lg font-bold text-black md:py-6 md:text-xl">
												{t(spec.labelKey)}
											</td>
											<td className="py-5 pr-4 text-sm text-black md:py-6 md:text-base">
												{t(`${spec.labelKey}Col1`)}
											</td>
											<td className="py-5 pr-4 text-sm text-black md:py-6 md:text-base">
												{t(`${spec.labelKey}Col2`)}
											</td>
											{columnCount >= 3 && (
												<td className="py-5 text-sm text-black md:py-6 md:text-base">
													{t(`${spec.labelKey}Col3`)}
												</td>
											)}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					) : (
						<div className="w-full">
							<div className="grid grid-cols-[45%_55%] pb-6">
								<div></div>
								<h3 className="text-lg font-bold text-black md:text-xl">{t("specificationsTitle")}</h3>
							</div>

							{rowKeys.map((spec) => (
								<div
									key={spec.labelKey}
									className="grid grid-cols-[45%_55%] border-t border-gray-300 py-5 md:py-6"
								>
									<div className="text-sm font-bold text-black md:pl-8 md:text-base lg:px-12 2xl:pl-44">
										{t(spec.labelKey)}
									</div>
									<div className="text-sm text-black md:text-base">{t(`${spec.labelKey}Value`)}</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		</section>
	);
}
